import Environment from '../../environment';
import axios from 'axios';
import {onFulfilled} from './interceptors/request';
import {handleLogout} from '@/Utils/CommonUtils';
import {useNavigation} from '@react-navigation/native';
import {ScreenName} from '@/navigators/StackConstants';

const useChatRestClient = () => {
  const navigation = useNavigation<any>();

  const ChatRestClient = axios.create({
    baseURL: Environment.CHAT_API_URL,
  });

  ChatRestClient.interceptors.request.use(onFulfilled);

  // AWS WAF token — sent as a header so it works cross-domain from localhost
  // dev and any non-circlechess.cc origin. Fails open if the SDK script isn't
  // loaded (e.g. WAF_CHALLENGE_SCRIPT_URL not set, or local dev without it).
  ChatRestClient.interceptors.request.use(async config => {
    if (config.url?.includes('send_otp')) {
      try {
        const token = await (window as any).AwsWafIntegration?.getToken();
        if (token) {
          config.headers['x-aws-waf-token'] = token;
        }
      } catch (e) {
        console.warn('AwsWafIntegration.getToken() failed', e);
      }
    }
    return config;
  });

  ChatRestClient.interceptors.response.use(
    response => {
      // If the request was made and the server responded with a status code
      // that falls out of the range of 2xx, this function will not be called.
      return response;
    },
    error => {
      // The OTP endpoints are the pre-login calls, and a 403 on them is the
      // WAF block action (rate limit), not a dead session -- signing the user
      // out and bouncing them to Login there both loses the error message and
      // logs out anyone who happens to be signed in already.
      const isOtpEndpoint = /auth\/v1\/(send|verify)_otp/.test(
        error?.config?.url || '',
      );
      // If request was sent and server responded with a status outside of the 2xx range
      if (error.response && !isOtpEndpoint) {
        switch (error.response.status) {
          case 401:
            // Handle 401 error code.
            handleLogout(() => {
              navigation?.navigate(ScreenName?.Login);
            });
            console.error('401 Unauthorized Error');
            break;
          case 403:
            // Handle 403 error code.
            handleLogout(() => {
              navigation?.navigate(ScreenName?.Login);
            });
            console.error('403 Forbidden Error');
            break;
          default:
            break;
        }
      }
      // A request that never got a response (network drop, CORS, timeout) has
      // no `error.response`; reading `.data` off it threw a TypeError inside
      // the interceptor, so the caller's onError saw that instead of the
      // failure. Keep the status alongside the body so callers can tell a
      // rate-limit block from an app-level error.
      if (!error.response) {
        return Promise.reject(error);
      }
      const body = error.response.data;
      return Promise.reject(
        body && typeof body === 'object'
          ? {...body, status: error.response.status}
          : {status: error.response.status, error: body},
      );
    },
  );
  return ChatRestClient;
};
export default useChatRestClient;
